import { FolderIcon, HomeIcon, PlusIcon, TableIcon } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCogfy, useCollections, usePermissions, useTitle, useToasts, useUserEvent } from '../../hooks'
import { BreadcrumbItem, Breadcrumbs, Card, Container } from '../../components'
import { Navbar } from '../../components/Navbar'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { CreateCollectionCommand } from '@indigohive/cogfy-types/endpoints/createCollection'

export function CollectionsIndexPage () {
  const params = useParams<{ workspace: string }>()
  const workspace = params.workspace!
  const getCollectionsPage = useCollections({ parentId: null })
  const { t } = useTranslation()
  const permissions = usePermissions()
  const canCreateCollection = permissions.isAdmin
  const registerUserEvent = useUserEvent()
  const toasts = useToasts()
  const cogfy = useCogfy()
  const navigate = useNavigate()

  useTitle({
    title: t('Collections'),
    loading: getCollectionsPage.isLoading,
    error: getCollectionsPage.isError
  })

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: t('Home'),
      icon: <HomeIcon className="w-4 h-4" />
    }
  ]

  const createCollection = useMutation({
    mutationFn: (data: CreateCollectionCommand) => cogfy.createCollection(data),
    onSuccess: result => {
      navigate(`/${workspace}/${result.id}`)
      toasts.success(t('collectionTemplatesPage:Collection created'))
    },
    onError: () => {
      toasts.error(t('collectionTemplatesPage:Error creating collection'))
    }
  })

  return (
    <>
      <Navbar>
        <Breadcrumbs items={breadcrumbItems} />
      </Navbar>
      <h1 className="text-2xl font-bold px-4 py-2 my-1 mx-2">{t('Collections')}</h1>
      <Container>
        {getCollectionsPage.isLoading && `${t('Loading')}...`}
        {getCollectionsPage.error && t('Error')}
        <Card>
          {getCollectionsPage.data?.data.length === 0 && (
            <ul className="menu w-full">
              <li>
                <a onClick={() => createCollection.mutate({})}>
                  <PlusIcon className="w-5 h-5" />
                  {canCreateCollection ? t('collectionAndFolderActions:New empty collection') : t('No collections found')}
                </a>
              </li>
            </ul>
          )}
          {getCollectionsPage.data && getCollectionsPage.data.data.length > 0 && (
            <ul className="menu w-full">
              {getCollectionsPage.data.data.map(collection => (
                <li key={collection.id}>
                  <Link
                    to={`/${workspace}/${collection.id}`}
                    onClick={() => {
                      registerUserEvent.mutate({
                        type: 'collection_selected',
                        data: {
                          collectionId: collection.id,
                          name: collection.name,
                          type: collection.type,
                          from: 'list'
                        }
                      })
                    }}
                  >
                    {collection.type === 'database'
                      ? <TableIcon size={16} />
                      : <FolderIcon size={16} />}
                    {collection.name ?? <span className="opacity-40">{t('Untitled')}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </Container>
    </>
  )
}
